import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { PageReactDataTable, Stack } from '@metarouter-private/mr-mui';
import { useFormattedAuditRows, PAGE_SIZE } from '@ion/api/audit-logs/query';
import isAllowedToReadAuditLogDebugData from '@ion/api/audit-logs/isAllowedToReadAuditLogDebugData';

import buildAuditLogTableColumns from './buildAuditLogTableColumns';
import buildExpansionProps from './buildExpansionProps';

const NOOP_FETCH_PAGE = () => {};
const HIDE_PAGINATION_FOOTER = () => null;

export default function AuditLogTable({
  rows,
  loading,
  hasActiveFilters,
  pagination,
  showPagination,
  onFetchPage,
  sortDirection,
  onSort,
  paginationResetDefaultPage,
}) {
  const [expandedRowId, setExpandedRowId] = useState(null);
  const { formattedEntryByRowId } = useFormattedAuditRows({ rows, hasActiveFilters, sortDirection });
  const showDebugData = isAllowedToReadAuditLogDebugData();

  const toggleRow = useCallback(rowId => setExpandedRowId(current => (current === rowId ? null : rowId)), []);

  const columns = useMemo(
    () => buildAuditLogTableColumns({ expandedRowId, onToggle: toggleRow, showDebugData }),
    [expandedRowId, toggleRow, showDebugData]
  );

  const effectivePagination = pagination ?? { count: rows.length };

  return (
    <Stack data-test-id="auditLogTable">
      <PageReactDataTable
        columns={columns}
        data={rows}
        pagination={effectivePagination}
        perPage={PAGE_SIZE}
        loading={loading}
        onFetchPage={onFetchPage}
        paginationResetDefaultPage={paginationResetDefaultPage}
        paginationComponent={showPagination ? undefined : HIDE_PAGINATION_FOOTER}
        sortServer
        defaultSortFieldId="timestamp"
        defaultSortAsc={sortDirection === 'asc'}
        onSort={(column, nextDirection) => onSort(nextDirection)}
        {...buildExpansionProps({ showDebugData, expandedRowId, formattedEntryByRowId })}
      />
    </Stack>
  );
}

AuditLogTable.propTypes = {
  rows: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  hasActiveFilters: PropTypes.bool.isRequired,
  pagination: PropTypes.shape({ count: PropTypes.number }),
  showPagination: PropTypes.bool,
  onFetchPage: PropTypes.func,
  sortDirection: PropTypes.oneOf(['desc', 'asc']).isRequired,
  onSort: PropTypes.func.isRequired,
  paginationResetDefaultPage: PropTypes.bool,
};

AuditLogTable.defaultProps = {
  pagination: null,
  showPagination: false,
  onFetchPage: NOOP_FETCH_PAGE,
  paginationResetDefaultPage: false,
};
